import React, { useEffect, useState } from "react";
import { IonPage, IonContent, IonHeader, IonToolbar, IonTitle, useIonAlert } from "@ionic/react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStudent } from "../contexts/StudentContext";
import { usePremium } from "../contexts/PremiumContext";
import { logout } from "../utils/session";
import { track } from "../services/analytics";
import { notificationPrefs } from "../services/notificationService";

const Row: React.FC<{ icon: string; label: string; value?: string; onClick?: () => void; danger?: boolean }> = ({ icon, label, value, onClick, danger }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    className={`w-full flex items-center gap-3 px-4 py-3.5 text-left border-b border-white/10 last:border-0 ${danger ? "text-red-300" : "text-gray-100"}`}>
    <i className={`fa-solid ${icon} w-5 text-center ${danger ? "text-red-400" : "text-primary-300"}`} />
    <span className="flex-1 text-sm font-semibold">{label}</span>
    {value && <span className="text-xs text-gray-400">{value}</span>}
    {onClick && <i className="fa-solid fa-chevron-right text-gray-600 text-xs" />}
  </button>
);

const Profile: React.FC = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { user, profile, stats, isParent } = useStudent();
  const { isPremium } = usePremium();
  const [presentAlert] = useIonAlert();
  const [build, setBuild] = useState<{ version: string; date: string } | null>(null);

  useEffect(() => {
    fetch("/buildStamp.json", { cache: "no-cache" })
      .then((r) => r.json())
      .then(setBuild)
      .catch(() => undefined);
  }, []);

  const confirmLogout = () =>
    presentAlert({
      header: t("profile.logout"),
      message: t("profile.logoutConfirm"),
      buttons: [
        { text: t("common.cancel"), role: "cancel" },
        { text: t("profile.logout"), role: "destructive", handler: () => logout() },
      ],
    });

  const toggleLang = () => {
    const next = i18n.language.startsWith("fr") ? "en" : "fr";
    i18n.changeLanguage(next);
  };

  if (!profile) return null;
  const reminders = notificationPrefs(profile);
  const pad = (n: number) => String(n).padStart(2, "0");

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonTitle>{t("profile.title")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="page-container wide px-4 py-3 space-y-4 animate-fade-in md:grid md:grid-cols-2 md:gap-4 md:space-y-0 md:items-start">
          <div className="card p-5 flex items-center gap-4 md:col-span-2">
            <div className="w-16 h-16 rounded-2xl bg-gradient-primary flex items-center justify-center text-3xl shadow-glow-primary">{profile.avatar || "🎓"}</div>
            <div className="flex-1 min-w-0">
              <p className="text-xl font-black text-white truncate">{profile.displayName}</p>
              <p className="text-sm text-gray-400">
                {isParent
                  ? t("profile.parentRole")
                  : `${t("common.yearShort", { year: profile.year })} · ${t("dashboard.level", { level: stats.level })} · ${stats.xp} XP`}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>

          {/* Premium : offre élève (l'offre famille passera par ici plus tard) */}
          {!isParent && (
            <button
              type="button"
              onClick={() => {
                track("paywall_viewed", { context: "profile" });
                history.push("/home/premium");
              }}
              className={`w-full card p-4 text-left flex items-center gap-3 ${isPremium ? "border-accent-500/40 bg-accent-500/10" : "border-primary-500/40 bg-primary-500/10"}`}>
              <i className={`fa-solid ${isPremium ? "fa-crown text-accent-300" : "fa-gem text-primary-300"} text-xl`} />
              <div className="flex-1">
                <p className="font-bold text-white">{isPremium ? t("profile.premiumActive") : t("profile.premiumCta")}</p>
                <p className="text-xs text-gray-400">{isPremium ? t("profile.premiumManage") : t("profile.premiumHint")}</p>
              </div>
              <i className="fa-solid fa-chevron-right text-gray-500 text-xs" />
            </button>
          )}

          <div className="card overflow-hidden">
            <Row icon="fa-user-pen" label={t("profile.edit")} onClick={() => history.push("/home/edit-profile")} />
            <Row icon="fa-shield-halved" label={t("profile.account")} value={user?.isAnonymous ? t("auth.guest.dashboardCta") : undefined} onClick={() => history.push("/home/account")} />
            {!isParent && <Row icon="fa-medal" label={t("progress.badges")} onClick={() => history.push("/home/badges")} />}
            {!isParent && (
              <Row icon="fa-bullseye" label={t("profileSetup.dailyGoal")} value={`${profile.dailyGoalMinutes} min`} onClick={() => history.push("/home/edit-profile")} />
            )}
            {!isParent && (
              <Row
                icon="fa-brain"
                label={t("profile.learning")}
                value={profile.learningProfile?.completedAt ? undefined : t("learning.todo")}
                onClick={() => history.push("/home/learning-profile")}
              />
            )}
            {!isParent && (
              <Row
                icon="fa-bell"
                label={t("profile.reminders")}
                value={reminders.enabled ? `${pad(reminders.hour)}:${pad(reminders.minute)}` : t("notifications.off")}
                onClick={() => history.push("/home/notifications")}
              />
            )}
            <Row icon="fa-language" label={t("profile.language")} value={i18n.language.startsWith("fr") ? "Français" : "English"} onClick={toggleLang} />
          </div>

          <div className="card overflow-hidden">
            {isParent ? (
              <Row icon="fa-children" label={t("profile.children")} onClick={() => history.push("/home/parent")} />
            ) : (
              <Row icon="fa-people-roof" label={t("profile.parents")} onClick={() => history.push("/home/parent-access")} />
            )}
            <Row icon="fa-file-shield" label={t("profile.privacy")} onClick={() => window.open("https://scholeo.ch/privacy", "_blank")} />
            <Row icon="fa-right-from-bracket" label={t("profile.logout")} onClick={confirmLogout} danger />
          </div>

          <p className="text-center text-[11px] text-gray-600 md:col-span-2">
            Scholeo {build?.version || ""} {build?.date ? `· ${build.date.slice(0, 10)}` : ""}
          </p>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Profile;
